import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmActionModal from "../../components/Common/ConfirmActionModal";
import TableContainer from "../../components/Common/TableContainer";
import {
  CountInfoTip,
  RecentUsageTable,
  SendingStatusBadge,
  UsageBar,
  planLabel,
} from "../Usage/UsageParts";
import {
  formatDate,
  formatDateTime,
  formatMonth,
  formatNumber,
} from "../Usage/usageFormat";
import {
  getCurrentMonth,
  getTenantPlan,
  getTenantUsage,
  planForMonth,
  setTenantSending,
  subscribeSendingChanges,
} from "../Usage/usageApi";

const prefix = "tenants.details.sendingControl";
const planPrefix = "tenants.details.plan";

// Plan, this month's usage, the last 6 months and pause / resume in one card
// (SUPER_ADMIN, ADMIN). Plan and contract data are synced from Airtable and
// read only here.
const TenantUsageCard = ({ tenant }) => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const [usage, setUsage] = useState(null);
  const [planData, setPlanData] = useState(null); // null: no plan yet
  const [loaded, setLoaded] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "pause" | "resume"
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const [nextUsage, nextPlan] = await Promise.all([
        getTenantUsage(tenant.id),
        getTenantPlan(tenant.id),
      ]);
      setUsage(nextUsage);
      setPlanData(nextPlan);
      setLoaded(true);
    } catch (err) {
      // Keep what is already on screen.
    }
  }, [tenant.id]);

  // Reload when the sending status changes.
  useEffect(() => {
    load();
    return subscribeSendingChanges(load);
  }, [load]);

  const handleConfirm = async () => {
    const action = confirmAction;
    // Close the dialog first so the button shows "Pausing…" / "Resuming…".
    setConfirmAction(null);
    setSubmitting(true);
    try {
      await setTenantSending(tenant.id, action);
      toast.success(
        t(
          action === "pause"
            ? `${prefix}.toast.paused`
            : `${prefix}.toast.resumed`
        )
      );
    } catch (err) {
      toast.error(
        t(
          action === "pause"
            ? `${prefix}.toast.pauseFailed`
            : `${prefix}.toast.resumeFailed`
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const paused = usage?.sendingStatus === "paused";
  const lastAction = usage?.lastAction;
  const thisMonth = usage?.months.find((m) => m.month === getCurrentMonth());
  const currentPlan = planForMonth(planData?.history, getCurrentMonth());
  const hasPlan = planData?.history?.length > 0;
  // With one record the history only repeats the plan shown on the card.
  const planChanged = planData?.history?.length > 1;

  const quotaText = (quota) =>
    t(`${planPrefix}.quotaValue`, { count: formatNumber(quota, lng) });
  const priceText = (price) =>
    t(`${planPrefix}.unitPriceValue`, { price: Number(price).toFixed(2) });

  const planFields = currentPlan
    ? [
        [t(`${planPrefix}.fields.plan`), planLabel(t, currentPlan.plan)],
        [t(`${planPrefix}.fields.monthlyQuota`), quotaText(currentPlan.quota)],
        [t(`${planPrefix}.fields.unitPrice`), priceText(currentPlan.unitPrice)],
        [
          t(`${planPrefix}.fields.contractPeriod`),
          t(`${planPrefix}.contractPeriodValue`, {
            start: formatDate(planData.contractStart, lng),
            end: formatDate(planData.contractEnd, lng),
          }),
        ],
      ]
    : [];

  const renderPlanField = ([label, value], index) => (
    <Col key={label} xs={6}>
      <p className="text-muted mb-1">
        {label}
        {/* Plan history, read only, in a dialog */}
        {index === 0 && planChanged && (
          <Button
            color="link"
            className="p-0 ms-2 fs-12 align-baseline"
            onClick={() => setShowHistory(true)}
          >
            {t(`${planPrefix}.viewHistory`)}
          </Button>
        )}
      </p>
      <p className="fw-semibold fs-15 mb-0">{value}</p>
    </Col>
  );

  const thisMonthContent = thisMonth && (
    <>
      <p className="text-muted mb-1">
        {t("usage.tenant.used")}
        <CountInfoTip />
      </p>
      {thisMonth.quota ? (
        <UsageBar
          messageCount={thisMonth.messageCount}
          quota={thisMonth.quota}
          showCounts
          large
        />
      ) : (
        <p className="fw-semibold fs-15 mb-0">
          {t("usage.messageCountValue", {
            count: formatNumber(thisMonth.messageCount, lng),
          })}
        </p>
      )}
    </>
  );

  const historyColumns = [
    {
      Header: t(`${planPrefix}.history.columns.effectiveMonth`),
      accessor: "effectiveMonth",
      Cell: ({ value }) => formatMonth(value, lng),
    },
    {
      Header: t(`${planPrefix}.history.columns.plan`),
      accessor: "plan",
      Cell: ({ value }) => planLabel(t, value),
    },
    {
      Header: t(`${planPrefix}.history.columns.monthlyQuota`),
      accessor: "quota",
      Cell: ({ value }) => quotaText(value),
    },
    {
      Header: t(`${planPrefix}.history.columns.unitPrice`),
      accessor: "unitPrice",
      Cell: ({ value }) => priceText(value),
    },
    {
      Header: t(`${planPrefix}.history.columns.syncedAt`),
      accessor: "syncedAt",
      Cell: ({ value }) => formatDateTime(value, lng),
    },
  ];

  const pauseButton = paused ? (
    <Button
      color="success"
      className="btn-card-action"
      onClick={() => setConfirmAction("resume")}
      disabled={submitting}
    >
      {submitting ? (
        <>
          <Spinner size="sm" className="me-2" />
          {t(`${prefix}.resuming`)}
        </>
      ) : (
        <>
          <i className="ri-play-circle-line me-2"></i>
          {t(`${prefix}.resume`)}
        </>
      )}
    </Button>
  ) : (
    <Button
      color="primary"
      outline
      className="btn-card-action"
      onClick={() => setConfirmAction("pause")}
      disabled={submitting}
    >
      {submitting ? (
        <>
          <Spinner size="sm" className="me-2" />
          {t(`${prefix}.pausing`)}
        </>
      ) : (
        <>
          <i className="ri-pause-circle-line me-2"></i>
          {t(`${prefix}.pause`)}
        </>
      )}
    </Button>
  );

  return (
    <>
      <Card className="mb-4">
        <CardHeader className="d-flex flex-wrap align-items-center gap-3">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2">
              <h5 className="card-title mb-0">{t(`${prefix}.heading`)}</h5>
              {usage && <SendingStatusBadge status={usage.sendingStatus} />}
            </div>
            {/* Who paused and since when; not needed once resumed */}
            {paused && lastAction && (
              <p className="text-muted mb-0 mt-1 fs-13">
                {t(`${prefix}.lastAction.${lastAction.type}`, {
                  name: lastAction.by,
                  time: formatDateTime(lastAction.at, lng),
                })}
              </p>
            )}
            {/* No edit button: plan and contract data come from Airtable.
                Before the first sync, the notSetHint alert says it instead. */}
            {(!loaded || hasPlan) && (
              <p className="text-muted mb-0 mt-1 fs-13">
                {t(`${planPrefix}.syncedHint`)}
              </p>
            )}
          </div>
          {loaded && pauseButton}
        </CardHeader>
        {loaded && (
          <CardBody>
            {/* Left half: the four plan fields. Right half: this month's
                usage bar. */}
            {currentPlan ? (
              <Row className="g-4 mb-4">
                <Col lg={6}>
                  <Row className="g-3">{planFields.map(renderPlanField)}</Row>
                </Col>
                {thisMonthContent && <Col lg={6}>{thisMonthContent}</Col>}
              </Row>
            ) : (
              <>
                <Alert
                  color="info"
                  className="d-flex align-items-center gap-2 mb-4"
                >
                  <i className="ri-information-line fs-16"></i>
                  <span>
                    {t(
                      hasPlan
                        ? `${planPrefix}.notStartedHint`
                        : `${planPrefix}.notSetHint`
                    )}
                  </span>
                </Alert>
                {thisMonthContent && (
                  <div className="mb-4 col-md-6">{thisMonthContent}</div>
                )}
              </>
            )}

            {/* Last 6 months */}
            <h6 className="mb-3">{t("usage.recent.heading")}</h6>
            <RecentUsageTable months={usage.months} />
          </CardBody>
        )}
      </Card>

      {showHistory && (
        <Modal
          isOpen
          toggle={() => setShowHistory(false)}
          centered
          size="lg"
        >
          <ModalHeader toggle={() => setShowHistory(false)}>
            {t(`${planPrefix}.history.heading`)}
          </ModalHeader>
          <ModalBody>
            <TableContainer
              columns={historyColumns}
              data={planData.history}
              loading={false}
              pageCount={1}
              hasPreviousPage={false}
              hasNextPage={false}
              pageParams={{
                currentPage: 1,
                rowsPerPage: planData.history.length,
              }}
              setPageParams={() => {}}
              disableRowModal={true}
              isGlobalFilter={false}
              tableClass="custom-header-css mb-0"
              theadClass="text-muted"
            />
          </ModalBody>
        </Modal>
      )}

      <ConfirmActionModal
        show={confirmAction !== null}
        onConfirm={handleConfirm}
        onClose={() => setConfirmAction(null)}
        title={t(
          confirmAction === "resume"
            ? `${prefix}.confirmResume.title`
            : `${prefix}.confirmPause.title`
        )}
        message={t(
          confirmAction === "resume"
            ? `${prefix}.confirmResume.message`
            : `${prefix}.confirmPause.message`,
          { tenantName: tenant.name }
        )}
        confirmText={t(
          confirmAction === "resume"
            ? `${prefix}.confirmResume.confirm`
            : `${prefix}.confirmPause.confirm`
        )}
        confirmColor={confirmAction === "resume" ? "success" : "danger"}
        user={undefined}
        tenant={{ name: tenant.name, portalId: tenant.portalId }}
      />
    </>
  );
};

export default TenantUsageCard;
